import React, {ReactElement, useContext} from 'react'
import {Actual, ApplyStatusInfo, Plan} from '../../repository/NatGetApplicationStatus'
import {
    Box,
    Step,
    StepConnector,
    StepContent,
    StepContentProps,
    StepIconProps,
    StepLabel,
    Stepper,
    Table,
} from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import {StyledTableCaption} from '../../../../../styledComponents/adminPage/Table'
import {Check, Close, PlaylistAddCheck} from '@mui/icons-material'
import styled from 'styled-components'
import {Colors, FontWeights} from '../../../../../styledComponents/Constants'
import {Nullable} from '../../../../../app/TypeHelpers'
import {
    LeftColumnFlexContainer,
    LeftRowFlexContainer,
    TopRowFlexContainer,
} from '../../../../../styledComponents/FlexComponents'
import {StyledDiv} from '../../../../../styledComponents/StyledTypeDefinitions'
import {
    APPLY_IRREGULAR_PATTERN,
    RESIDENT_APPLY_PROCESS,
    RESIDENT_APPLY_STATUS,
    ROLE_FOR_ADMINISTRATOR,
} from '../../../../../CodeMaster'
import {getApplicant} from '../helper/NatApplicationHelper'
import {AppAdminContext} from '../../../../../app/AppAdminContext'

interface Props {
    currentApplyStatus: string
    applyStatusInfoList: ApplyStatusInfo[]
    additionalInfo?: Nullable<ApplicationStatusAdditionalInfo>
}

interface Steps {
    process: string
    rep: Nullable<string>
    responsibleName: Nullable<string>
    commentTitle: Nullable<string>
    comment: Nullable<string>
    note: Nullable<string>
    status: Nullable<string>
    additionalInfo?: Nullable<ApplicationStatusAdditionalInfo>
    registerDatetime: Nullable<string>
}

export interface ApplicationStatusAdditionalInfo {
    targetProcess: RESIDENT_APPLY_PROCESS
    commentTitle: Nullable<string>
    comment: Nullable<string>
}

export const NatApplicationStatusComponent: React.FC<Props> = props => {
    const {currentApplyStatus, applyStatusInfoList, additionalInfo} = props
    const spMode = useContext(AppAdminContext).showSpModePage

    const getAdminResponsibleName = (applyStatus: Nullable<string>, actual: Actual, plan: Plan): string => {
        if (applyStatus) {
            if (!actual.roleId) {
                return ''
            }
            return ROLE_FOR_ADMINISTRATOR.codeToEnumConstants(actual.roleId).label +
                '　' +
                actual.familyName +
                ' ' +
                actual.firstName
        } else {
            return plan.rep
        }
    }

    const getApplyStatusSteps = (): Steps[] => {
        const steps: Steps[] = []
        for (const applyStatusInfo of applyStatusInfoList) {
            const {applyProcess, applyStatus, actual, plan, processName} = applyStatusInfo

            let process: string = ''
            let rep: string = ''
            let responsibleName: string = ''
            let commentTitle: Nullable<string> = null
            let note: Nullable<string> = null

            switch (applyProcess) {
                case RESIDENT_APPLY_PROCESS.APPLY.value:
                    process = processName ?? '提出'
                    rep = '申請者'
                    responsibleName = getApplicant(
                        actual.ridgeName,
                        actual.roomNumber,
                        actual.familyName,
                        actual.firstName,
                    )
                    break
                case RESIDENT_APPLY_PROCESS.RECEPTION.value:
                    process = processName ?? '受付'
                    rep = '担当者'
                    responsibleName = getAdminResponsibleName(applyStatus, actual, plan)
                    if (actual.comment) {
                        commentTitle = '理事長への連絡事項'
                    }
                    break
                case RESIDENT_APPLY_PROCESS.PET_CHAIRMAN_APPROVAL.value:
                    process = processName ?? '承認'
                    responsibleName = getAdminResponsibleName(applyStatus, actual, plan)
                    if (actual.comment) {
                        commentTitle = '理事長への連絡事項'
                    }
                    if (applyStatus) {
                        rep = '代行操作者'
                        if (actual.irregularPattern === APPLY_IRREGULAR_PATTERN.NO_PET_CLUB.value) {
                            note = 'ペットクラブがないため、承認として処理しました。'
                        } else {
                            if (applyStatus === RESIDENT_APPLY_STATUS.REJECTION.value) {
                                note = 'ペットクラブ委員長が申請の内容について却下と判断し、操作を代行しました。'
                            } else {
                                note = 'ペットクラブ委員長に申請の内容について承認をいただいたので、操作を代行しました。'
                            }
                        }
                    } else {
                        rep = '担当者'
                    }
                    break
                case RESIDENT_APPLY_PROCESS.ASSOC_DIRECTOR_APPROVAL.value:
                    process = processName ?? '承認'
                    responsibleName = getAdminResponsibleName(applyStatus, actual, plan)
                    if (actual.comment) {
                        commentTitle = 'コメント'
                    }
                    if (actual.irregularPattern === APPLY_IRREGULAR_PATTERN.ASSOC_DIRECTOR_DELEGATE_APPROVAL.value) {
                        rep = '代行操作者'
                        if (applyStatus === RESIDENT_APPLY_STATUS.REJECTION.value) {
                            note = '書面などによる理事長の判断に基づき、却下の操作を代行しました。'
                        } else {
                            note = '書面などによる理事長の判断に基づき、承認の操作を代行しました。'
                        }
                    } else {
                        rep = '担当者'
                    }
                    break
            }

            steps.push({
                process: applyStatus === RESIDENT_APPLY_STATUS.REJECTION.value
                    ? '却下'
                    : process,
                rep: rep,
                responsibleName: responsibleName,
                commentTitle: applyStatus === RESIDENT_APPLY_STATUS.REJECTION.value
                    ? '却下理由'
                    : commentTitle,
                comment: actual.comment ?? null,
                note: note,
                status: applyStatus,
                additionalInfo: additionalInfo?.targetProcess.value === applyProcess ? additionalInfo : null,
                registerDatetime: actual.registerDatetime,
            })

            if (applyStatus === RESIDENT_APPLY_STATUS.REJECTION.value ||
                applyStatus === RESIDENT_APPLY_STATUS.COMPLETE.value) {
                break
            }
        }

        if (currentApplyStatus !== RESIDENT_APPLY_STATUS.REJECTION.value) {
            const last = steps.at(-1)
            steps.push({
                process: '完了',
                rep: null,
                responsibleName: null,
                commentTitle: null,
                comment: null,
                note: null,
                status: currentApplyStatus,
                registerDatetime: last?.registerDatetime ?? null,
            })
        }

        return steps
    }

    const applyStatusSteps = getApplyStatusSteps()

    let activeStep
    if (currentApplyStatus === RESIDENT_APPLY_STATUS.COMPLETE.value ||
        currentApplyStatus === RESIDENT_APPLY_STATUS.REJECTION.value) {
        activeStep = 99
    } else {
        activeStep = applyStatusSteps.findIndex(step => step.status === currentApplyStatus) + 1
    }

    const StepIconComponent = (stepIconProps: StepIconProps): ReactElement => {
        const {active, completed} = stepIconProps

        return (
            <StepIconContainer $active={active}>
                {completed ? <CheckIcon/> : <CircleIcon/>}
            </StepIconContainer>
        )
    }

    const StepRejectionIconComponent = (stepIconProps: StepIconProps): ReactElement => {
        const {active} = stepIconProps

        return (
            <StepIconContainer $active={active}>
                <CloseIcon/>
            </StepIconContainer>
        )
    }

    const ApplicationStatusComponentPc = (activeStepProp: number): ReactElement => {
        return (
            <>
                <Table>
                    <StyledTableCaption>進行状況</StyledTableCaption>
                </Table>
                <Box>
                    <StyledStepperPc orientation="vertical" activeStep={activeStepProp} connector={<StepperStyles/>}>
                        {applyStatusSteps.map((step, index) => (
                            <Step expanded={true} key={index}>
                                <StepLabel style={{height: '25px'}}
                                           StepIconComponent={(step.status === RESIDENT_APPLY_STATUS.REJECTION.value)
                                    ? StepRejectionIconComponent
                                    : StepIconComponent
                                }>
                                    <TopRowFlexContainer>
                                        <OutlineContainer>
                                            <ProcessTitle>{step.process}</ProcessTitle>
                                        </OutlineContainer>
                                        <DetailContainer>
                                            {step.rep &&
                                                <ItemTitleAreaPc highlight={step.rep === '代行操作者'}>
                                                    {step.rep}
                                                </ItemTitleAreaPc>
                                            }
                                        </DetailContainer>
                                    </TopRowFlexContainer>
                                </StepLabel>
                                <StyledStepContent incomplete={step.status === null}>
                                    <TopRowFlexContainer>
                                        <OutlineContainer>
                                            <ProcessCompleteDatePc>{step.registerDatetime}</ProcessCompleteDatePc>
                                        </OutlineContainer>
                                        <DetailContainer>
                                            {step.responsibleName}
                                            {step.note &&
                                                <NoteContainerPc><PlaylistAddCheckIcon/>{step.note}</NoteContainerPc>
                                            }
                                        </DetailContainer>
                                    </TopRowFlexContainer>
                                    {step.additionalInfo &&
                                        <>
                                            <Box mt={'16px'}>
                                                <TopRowFlexContainer>
                                                    <OutlineContainer/>
                                                    <DetailContainer>
                                                        <ItemTitleAreaPc>
                                                            {step.additionalInfo.commentTitle}
                                                        </ItemTitleAreaPc>
                                                    </DetailContainer>
                                                </TopRowFlexContainer>
                                                <TopRowFlexContainer>
                                                    <OutlineContainer/>
                                                    <DetailContainer>
                                                        {step.additionalInfo.comment}
                                                    </DetailContainer>
                                                </TopRowFlexContainer>
                                            </Box>
                                        </>
                                    }
                                    {step.comment &&
                                        <>
                                            <Box mt={'16px'}>
                                                <TopRowFlexContainer>
                                                    <OutlineContainer/>
                                                    <DetailContainer>
                                                        <ItemTitleAreaPc>
                                                            {step.commentTitle}
                                                        </ItemTitleAreaPc>
                                                    </DetailContainer>
                                                </TopRowFlexContainer>
                                                <TopRowFlexContainer>
                                                    <OutlineContainer/>
                                                    <DetailContainer>
                                                        {step.comment}
                                                    </DetailContainer>
                                                </TopRowFlexContainer>
                                            </Box>
                                        </>
                                    }
                                </StyledStepContent>
                            </Step>
                        ))}
                    </StyledStepperPc>
                </Box>
            </>
        )
    }

    const ApplicationStatusComponentSp = (activeStepProp: number): ReactElement => {
        return (
            <>
                <Box>
                    <StyledStepperSp orientation="vertical" activeStep={activeStepProp} connector={<StepperStyles/>}>
                        {applyStatusSteps.map((step, index) => (
                            <Step expanded={true} key={index}>
                                <StepLabel StepIconComponent={(step.status === RESIDENT_APPLY_STATUS.REJECTION.value)
                                    ? StepRejectionIconComponent
                                    : StepIconComponent
                                }>
                                    <LeftRowFlexContainer>
                                        <ProcessTitle>{step.process}</ProcessTitle>
                                        <ProcessCompleteDateSp>{step.registerDatetime}</ProcessCompleteDateSp>
                                    </LeftRowFlexContainer>
                                </StepLabel>
                                <StyledStepContent $active incomplete={step.status === null}>
                                    <DetailContainer>
                                        {step.rep &&
                                            <ItemTitleAreaSp highlight={step.rep === '代行操作者'}>
                                                {step.rep}
                                            </ItemTitleAreaSp>
                                        }
                                        <ResponsibleNameContainerSp>{step.responsibleName}</ResponsibleNameContainerSp>
                                        {step.note &&
                                            <NoteContainerSp><PlaylistAddCheckIcon/>{step.note}</NoteContainerSp>
                                        }
                                        {step.additionalInfo &&
                                            <>
                                                <Box mt={'8px'}/>
                                                <ItemTitleAreaSp>{step.additionalInfo.commentTitle}</ItemTitleAreaSp>
                                                <CommentContainerSp>{step.additionalInfo.comment}</CommentContainerSp>
                                            </>
                                        }
                                        {step.comment &&
                                            <>
                                                <Box mt={'8px'}/>
                                                <ItemTitleAreaSp>{step.commentTitle}</ItemTitleAreaSp>
                                                <CommentContainerSp>{step.comment}</CommentContainerSp>
                                            </>
                                        }
                                    </DetailContainer>
                                </StyledStepContent>
                            </Step>
                        ))}
                    </StyledStepperSp>
                </Box>
            </>
        )
    }

    return (
        <>
            {spMode ? ApplicationStatusComponentSp(activeStep) : ApplicationStatusComponentPc(activeStep)}
        </>
    )
}

const StepperStyles = withStyles({
    active: {
        '& $line': {
            '&:before': {
                background: Colors.AdminPrimary,
            },
        },
    },
    completed: {
        '& $line': {
            '&:before': {
                background: Colors.AdminPrimary,
            },
        },
    },
    vertical: {
        height: 40,
        padding: 0,
        position: 'relative',
    },
    line: {
        border: 'none',
        '&:before': {
            position: 'absolute',
            content: '""',
            width: 3,
            background: Colors.AccentLight,
            height: '100%',
            borderRadius: '0 0 1px 1px',
        },
    },
})(StepConnector)

const StyledStepperPc = withStyles({
    root: {
        padding: '24px 32px 40px 32px',
    },
})(Stepper)

const StyledStepperSp = withStyles({
    root: {
        padding: '24px 16px',
    },
})(Stepper)

const StepIconContainer = styled.div<{ $active?: boolean }>`
    color: ${props => props.$active ? Colors.AdminPrimary : Colors.AccentLight};
  width: 28px;
  height: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const CheckIcon = withStyles({
    root: {
        color: Colors.AdminPrimary,
        zIndex: 1,
        fontSize: 18,
    },
})(Check)

const CloseIcon = withStyles({
    root: {
        color: 'grey',
        stroke: 'white',
        strokeWidth: 0.5,
        zIndex: 1,
        fontSize: 24,
    },
})(Close)

const CircleIcon = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: currentColor;
`

const OutlineContainer = styled(LeftColumnFlexContainer)`
  width: 142px;
`

const DetailContainer = styled(LeftColumnFlexContainer)`
  flex: 1;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`

const ProcessTitle: StyledDiv = styled.div`
  font-size: 16px;
  font-weight: ${FontWeights.Bold};
`

const ProcessCompleteDatePc: StyledDiv = styled.div`
  font-size: 14px;
  color: ${Colors.AccentDark};
`

const ProcessCompleteDateSp: StyledDiv = styled.div`
  font-size: 14px;
  color: ${Colors.AccentDark};
  margin-left: 24px;
`

const ItemTitleAreaPc = styled.div<{ highlight?: boolean }>`
  font-size: 12px;
  background: ${props => props.highlight
          ? `linear-gradient(0deg, rgba(245, 166, 35, 0.2), rgba(245, 166, 35, 0.2)), ${Colors.Surface}`
          : Colors.Background};
  padding: 1px 16px;
  color: ${Colors.Body};
  margin-bottom: 4px;
  height: 20px;
  line-height: 18px;
`

const ItemTitleAreaSp = styled.div<{ highlight?: boolean }>`
  font-size: 12px;
  background: ${props => props.highlight
          ? `linear-gradient(0deg, rgba(245, 166, 35, 0.2), rgba(245, 166, 35, 0.2)), ${Colors.Surface}`
          : Colors.Background};
  padding: 1px 16px;
  color: ${Colors.Body};
  margin-top: 8px;
  height: 20px;
  line-height: 18px;
`

const CommentContainerSp = styled.div`
  margin-top: 4px;
`

const ResponsibleNameContainerSp = styled.div`
  margin-top: 4px;
`

const NoteContainerPc = styled.div`
  font-size: 12px;
  align-items: center;
  display: flex;
  padding-top: 4px;
  line-height: 18px;
`

const NoteContainerSp = styled.div`
  font-size: 12px;
  align-items: flex-start;
  display: flex;
  padding-top: 4px;
  line-height: 18px;
`

const StyledStepContent = styled(({incomplete, ...props}) =>
    (<StepContent {...props} />))<StepContentProps & { incomplete?: boolean }>`
  &.MuiStepContent-root {
    position: relative;
    margin-top: 0;
    padding-left: 23px;
    border-left: none;

    &:before {
      position: absolute;
      content: '';
      width: 3px;
      background: ${props => props.incomplete ? Colors.AccentLight : Colors.AdminPrimary};
      height: 100%;
      border-radius: 1px 1px 0 0;
      left: 0;
    }
  }

  &.MuiStepContent-last {
    border-left: none;

    &:before {
      position: absolute;
      content: none;
    }
  }
`

const PlaylistAddCheckIcon = withStyles({
    root: {
        color: Colors.AccentDark,
        fontSize: 18,
        marginRight: 2.75,
    },
})(PlaylistAddCheck)
